/* ==========================================================================
   KaryaGold — Transparent Sticky Header & Hero Slideshow controller
   ========================================================================== */

(function () {
  'use strict';

  const SOLID_THRESHOLD = 80;
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* --------------------------------------------------------------------
     Header transparent / solid controller
     -------------------------------------------------------------------- */
  class KgHeaderController {
    constructor() {
      const headerEl = document.querySelector('[data-kg-header]');
      this.header = headerEl
        ? headerEl.closest('.section-header') || headerEl
        : document.querySelector('.section-header');
      this.announcementBar = document.querySelector(
        '.shopify-section.announcement-bar-section, .shopify-section--announcement-bar, .utility-bar'
      );
      this.body = document.body;
      this.hasHero = !!document.querySelector('.kg-hero-slideshow');

      if (!this.hasHero) {
        this.body.classList.remove('kg-has-hero');
        return;
      }

      this.body.classList.add('kg-has-hero');
      this.ticking = false;
      this.lastSolid = null;

      this.measure();
      this.update();

      window.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
      window.addEventListener('resize', this.onResize.bind(this));

      // Keep --kg-ann-bar-height accurate as the announcement bar resizes
      // (multi-line announcement, breakpoint changes, etc.)
      if ('ResizeObserver' in window && this.announcementBar) {
        this.resizeObserver = new ResizeObserver(() => {
          this.measure();
          this.update();
        });
        this.resizeObserver.observe(this.announcementBar);
      }

      // Recalculate after fonts/images settle (announcement bar height may shift)
      window.addEventListener('load', () => {
        this.measure();
        this.update();
      });
    }

    measure() {
      const annBar = this.announcementBar;
      this.annHeight = annBar ? annBar.offsetHeight : 0;
      document.documentElement.style.setProperty(
        '--kg-ann-bar-height',
        this.annHeight + 'px'
      );
    }

    onResize() {
      this.measure();
      this.update();
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
    }

    update() {
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;

      // Header sits below announcement bar initially, slides up 1:1 with
      // scroll until it pins to the very top of the viewport.
      const translate = Math.max(0, this.annHeight - scrollY);
      document.documentElement.style.setProperty(
        '--kg-header-translate',
        translate + 'px'
      );

      // Toggle solid mode once user scrolls past the threshold (only when
      // it actually changes to avoid layout thrash).
      if (this.header) {
        const shouldBeSolid = scrollY > SOLID_THRESHOLD;
        if (shouldBeSolid !== this.lastSolid) {
          this.header.classList.toggle('kg-header--solid', shouldBeSolid);
          this.lastSolid = shouldBeSolid;
        }
      }
    }
  }

  /* --------------------------------------------------------------------
     <kg-hero-slideshow> custom element
     -------------------------------------------------------------------- */
  class KgHeroSlideshow extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.timer = null;
    }

    connectedCallback() {
      this.viewport = this.querySelector('.kg-hero-slideshow__viewport');
      this.slides = Array.from(this.querySelectorAll('.kg-hero-slideshow__slide'));
      this.dots = Array.from(this.querySelectorAll('.kg-hero-slideshow__dot'));
      this.arrows = Array.from(this.querySelectorAll('.kg-hero-slideshow__arrow'));

      if (!this.slides.length) return;

      // Initial active slide
      const initialActive = this.slides.findIndex((slide) =>
        slide.classList.contains('is-active')
      );
      this.currentIndex = initialActive >= 0 ? initialActive : 0;
      this.activate(this.currentIndex, true);

      // Read autoplay & speed from data attributes
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      const speedSeconds = parseFloat(this.dataset.speed) || 5;
      this.duration = speedSeconds * 1000;
      this.style.setProperty('--kg-slide-duration', speedSeconds + 's');

      // Bind interactions
      this.dots.forEach((dot) => {
        dot.addEventListener('click', (event) => {
          event.preventDefault();
          const index = parseInt(dot.getAttribute('data-index'), 10);
          if (!isNaN(index)) this.goTo(index, true);
        });
      });

      this.arrows.forEach((arrow) => {
        arrow.addEventListener('click', (event) => {
          event.preventDefault();
          const direction = arrow.getAttribute('data-direction');
          if (direction === 'next') this.next(true);
          else this.prev(true);
        });
      });

      // Pause on hover / focus
      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => this.play());
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', () => this.play());

      // Pause when tab not visible
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pause();
        else this.play();
      });

      // Respect reduced motion: disable autoplay
      if (reducedMotionQuery.matches) {
        this.autoplayEnabled = false;
      }

      this.play();
    }

    disconnectedCallback() {
      this.pause();
    }

    activate(index, skipAnimationReset) {
      this.slides.forEach((slide, i) => {
        const isActive = i === index;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      this.dots.forEach((dot, i) => {
        const isActive = i === index;
        dot.classList.toggle('is-active', isActive);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      // Restart progress bar animation by toggling the host class.
      // Removing → reflow → adding forces the keyframe animation to restart
      // for the freshly-active dot.
      if (!skipAnimationReset && this.autoplayEnabled) {
        this.classList.remove('is-autoplay');
        // Force reflow to ensure the animation restarts cleanly.
        // eslint-disable-next-line no-unused-expressions
        this.offsetWidth;
        this.classList.add('is-autoplay');
      }

      this.currentIndex = index;
    }

    goTo(index, userAction) {
      if (index === this.currentIndex) return;
      const len = this.slides.length;
      const nextIndex = ((index % len) + len) % len;
      this.activate(nextIndex);
      if (userAction) this.restartTimer();
    }

    next(userAction) {
      this.goTo(this.currentIndex + 1, userAction);
    }

    prev(userAction) {
      this.goTo(this.currentIndex - 1, userAction);
    }

    play() {
      if (!this.autoplayEnabled || this.slides.length < 2) {
        this.classList.remove('is-autoplay');
        return;
      }
      this.classList.remove('is-paused');
      this.classList.add('is-autoplay');
      this.clearTimer();
      this.timer = window.setInterval(() => this.next(false), this.duration);
    }

    pause() {
      this.classList.add('is-paused');
      this.clearTimer();
    }

    clearTimer() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    restartTimer() {
      if (this.autoplayEnabled) {
        this.clearTimer();
        this.play();
      }
    }
  }

  /* --------------------------------------------------------------------
     Hover-open for header-menu <details> elements (desktop only)
     Works for both mega panels and plain dropdowns.
     Touch/pointer-coarse devices keep the default click behaviour.
     -------------------------------------------------------------------- */
  function initMenuHover() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    const CLOSE_DELAY = 350; // ms — gives time to move cursor diagonally into the panel
    const sectionHeader = document.querySelector('.section-header');

    // kg-header--menu-active keeps the header solid while any menu is open or
    // animating closed. It is set on mouseenter and removed after CLOSE_DELAY
    // (matching the scheduleClose timer) so the background fade starts at the
    // same moment the mega panel's own close animation begins.
    let deactivateTimer = null;

    function activate() {
      clearTimeout(deactivateTimer);
      if (sectionHeader) sectionHeader.classList.add('kg-header--menu-active');
    }

    function scheduleDeactivate() {
      clearTimeout(deactivateTimer);
      deactivateTimer = setTimeout(() => {
        if (sectionHeader) sectionHeader.classList.remove('kg-header--menu-active');
      }, CLOSE_DELAY);
    }

    document.querySelectorAll('header-menu').forEach((menu) => {
      const details = menu.querySelector('details');
      if (!details) return;

      let closeTimer = null;

      const open = () => {
        clearTimeout(closeTimer);
        activate();
        if (!details.hasAttribute('open')) {
          details.setAttribute('open', '');
        }
      };

      const scheduleClose = () => {
        clearTimeout(closeTimer);
        scheduleDeactivate();
        closeTimer = setTimeout(() => {
          details.removeAttribute('open');
        }, CLOSE_DELAY);
      };

      menu.addEventListener('mouseenter', open);
      menu.addEventListener('mouseleave', scheduleClose);

      // The panel is position:absolute so it sits outside <header-menu>'s
      // bounding box. When the cursor moves from the trigger down into the
      // panel, mouseleave fires on <header-menu> before the cursor reaches
      // the panel. Listening directly on the panel cancels that close timer.
      const panel = details.querySelector('.mega-menu__content, .header__submenu');
      if (panel) {
        panel.addEventListener('mouseenter', open);
        panel.addEventListener('mouseleave', scheduleClose);
      }

      // Prevent the native click-toggle from fighting hover state
      const summary = details.querySelector('summary');
      if (summary) {
        summary.addEventListener('click', (e) => {
          // If already open via hover, clicking should just close
          if (details.hasAttribute('open')) {
            e.preventDefault();
            details.removeAttribute('open');
            if (sectionHeader) sectionHeader.classList.remove('kg-header--menu-active');
          }
        });
      }
    });
  }

  /* --------------------------------------------------------------------
     Init
     -------------------------------------------------------------------- */
  function init() {
    if (!customElements.get('kg-hero-slideshow')) {
      customElements.define('kg-hero-slideshow', KgHeroSlideshow);
    }
    // eslint-disable-next-line no-new
    new KgHeaderController();
    initMenuHover();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
